import {
  ctaThemeCssVariables,
  normalizeScanPageConfig,
  scanPageConfigCssVariables,
  type CtaTheme,
  type ScanPageConfig
} from '@adarveo/shared';
import { useQuery } from '@tanstack/react-query';
import Hls from 'hls.js';
import {
  ArrowUpRight,
  CircleAlert,
  Download,
  Loader2,
  Maximize2,
  Play,
  Settings,
  Share2,
  Sparkles,
  Volume2,
  VolumeX,
  X
} from 'lucide-react';
import { type CSSProperties, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { hasSupabaseConfig, supabase } from '../lib/supabase';
import { trackEvent } from '../lib/tracking';

type Experience = {
  publicSlug: string;
  adName: string;
  adStatus: string;
  campaignName: string;
  campaignObjective: string | null;
  scanPageConfig?: ScanPageConfig | null;
  markerUrl: string | null;
  videoUrl: string | null;
  mindFileUrl: string | null;
  ctaKicker: string | null;
  ctaHeadline: string | null;
  ctaBody: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  ctaTheme?: CtaTheme | null;
  ctaLogoUrl?: string | null;
};

type BrowserSupport = {
  supported: boolean;
  name: string;
};

type FullscreenVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
};

type AdPlayContext = 'ar' | 'direct' | 'fallback';

type MindARThreeConstructor = new (options: {
  container: HTMLElement;
  imageTargetSrc: string;
  filterMinCF?: number;
  filterBeta?: number;
  uiLoading?: 'yes' | 'no';
  uiScanning?: 'yes' | 'no';
}) => {
  cssRenderer?: {
    domElement: HTMLElement;
  };
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  video?: HTMLVideoElement;
  addAnchor: (targetIndex: number) => {
    group: THREE.Group;
    onTargetFound?: () => void;
    onTargetLost?: () => void;
  };
  start: () => Promise<void>;
  stop: () => Promise<void> | void;
};

type ArRuntime = {
  anchor: ReturnType<InstanceType<MindARThreeConstructor>['addAnchor']>;
  cleanupVideo: () => void;
  container: HTMLElement;
  mindarThree: InstanceType<MindARThreeConstructor>;
  renderer: THREE.WebGLRenderer;
  video: HTMLVideoElement;
};

const MINDAR_THREE_MODULE_URL =
  'https://esm.sh/mind-ar@1.2.5/dist/mindar-image-three.prod.js?deps=three@0.160.0';

const isHlsUrl = (url: string) => /\.m3u8(?:$|[?#])/i.test(url);

const attachVideoSource = (video: HTMLVideoElement, url: string) => {
  if (isHlsUrl(url) && Hls.isSupported() && !video.canPlayType('application/vnd.apple.mpegurl')) {
    const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
    hls.loadSource(url);
    hls.attachMedia(video);
    return () => hls.destroy();
  }

  video.src = url;
  return () => {
    video.removeAttribute('src');
    video.load();
  };
};

const loadImageAspect = (url: string) =>
  new Promise<number>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (!image.naturalWidth || !image.naturalHeight) {
        reject(new Error('Marker image has no dimensions'));
        return;
      }
      resolve(image.naturalWidth / image.naturalHeight);
    };
    image.onerror = () => reject(new Error('Marker image failed to load'));
    image.src = url;
  });

const fitTextureToAspect = (texture: THREE.Texture, sourceAspect: number, targetAspect: number) => {
  texture.center.set(0.5, 0.5);

  if (sourceAspect > targetAspect) {
    texture.repeat.set(targetAspect / sourceAspect, 1);
  } else {
    texture.repeat.set(1, sourceAspect / targetAspect);
  }

  texture.offset.set((1 - texture.repeat.x) / 2, (1 - texture.repeat.y) / 2);
  texture.needsUpdate = true;
};

const getBrowserSupport = (): BrowserSupport => {
  const ua = navigator.userAgent || '';
  const isSamsungInternet = /SamsungBrowser/i.test(ua);
  const isChrome =
    /(Chrome|CriOS)\/\d+/i.test(ua) &&
    !/(EdgA?|OPR|Opera|SamsungBrowser|DuckDuckGo|UCBrowser|YaBrowser|FxiOS)/i.test(ua);
  const isSafari =
    /Safari\/\d+/i.test(ua) &&
    /Version\/\d+/i.test(ua) &&
    !/(Chrome|CriOS|Chromium|Edg|OPR|Opera|SamsungBrowser|Firefox|FxiOS|DuckDuckGo)/i.test(ua);

  if (isChrome) return { supported: true, name: 'Chrome' };
  if (isSafari) return { supported: true, name: 'Safari' };
  if (isSamsungInternet) return { supported: false, name: 'Samsung Internet' };
  return { supported: false, name: 'your current browser' };
};

const buildChromeOpenUrl = (currentUrl: string) => {
  const isAndroid = /Android/i.test(navigator.userAgent || '');
  const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

  if (isAndroid) {
    const url = new URL(currentUrl);
    const pathWithQuery = `${url.pathname}${url.search}${url.hash}`;
    const fallback = encodeURIComponent(currentUrl);
    return `intent://${url.host}${pathWithQuery}#Intent;scheme=${url.protocol.replace(':', '')};package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
  }

  if (isIos) return currentUrl.replace(/^https:/i, 'googlechromes:').replace(/^http:/i, 'googlechrome:');
  return 'https://www.google.com/chrome/';
};

const isQrEntrySource = (source: string) => ['qr', 'qrcode', 'qr_code'].includes(source.toLowerCase());

const loadExperience = async (publicSlug: string) => {
  if (!supabase) throw new Error('Supabase is not configured');
  const { data, error } = await supabase.functions.invoke('get-public-ad-experience', {
    body: { publicSlug }
  });
  if (error) throw error;
  if (!data?.ok) throw new Error(data?.error ?? 'Experience unavailable');
  return data.experience as Experience;
};

const CTA_REVEAL_DELAY_MS = 2800;
const AR_START_TIMEOUT_MS = 12000;
const AR_MIN_START_FACING_DOT = 0.52;
const AR_STABLE_START_FRAMES = 6;
const ctaDefaults = {
  label: 'Create photos that come alive'
};

type PoseScratch = {
  cameraPosition: THREE.Vector3;
  targetNormal: THREE.Vector3;
  targetPosition: THREE.Vector3;
  targetQuaternion: THREE.Quaternion;
  toCamera: THREE.Vector3;
};

const createPoseScratch = (): PoseScratch => ({
  cameraPosition: new THREE.Vector3(),
  targetNormal: new THREE.Vector3(),
  targetPosition: new THREE.Vector3(),
  targetQuaternion: new THREE.Quaternion(),
  toCamera: new THREE.Vector3()
});

const targetIsFacingCamera = (target: THREE.Object3D, camera: THREE.Camera, scratch: PoseScratch) => {
  target.updateWorldMatrix(true, false);
  camera.updateWorldMatrix(true, false);
  target.getWorldPosition(scratch.targetPosition);
  camera.getWorldPosition(scratch.cameraPosition);
  target.getWorldQuaternion(scratch.targetQuaternion);

  scratch.targetNormal.set(0, 0, 1).applyQuaternion(scratch.targetQuaternion).normalize();
  scratch.toCamera.subVectors(scratch.cameraPosition, scratch.targetPosition).normalize();

  return Math.abs(scratch.targetNormal.dot(scratch.toCamera)) >= AR_MIN_START_FACING_DOT;
};

const styleMindArLayers = (
  mindarThree: InstanceType<MindARThreeConstructor>,
  container: HTMLElement,
  options: { hideCameraFeed?: boolean } = {}
) => {
  const hideCameraFeed = Boolean(options.hideCameraFeed);
  container.style.background = hideCameraFeed ? '#000' : 'transparent';
  container.style.isolation = 'isolate';

  const cameraVideo = mindarThree.video;
  if (cameraVideo) {
    cameraVideo.muted = true;
    cameraVideo.playsInline = true;
    cameraVideo.setAttribute('playsinline', 'true');
    cameraVideo.setAttribute('webkit-playsinline', 'true');
    cameraVideo.style.background = 'transparent';
    cameraVideo.style.height = '100%';
    cameraVideo.style.inset = '0';
    cameraVideo.style.left = '0';
    cameraVideo.style.maxHeight = 'none';
    cameraVideo.style.maxWidth = 'none';
    cameraVideo.style.minHeight = '100%';
    cameraVideo.style.minWidth = '100%';
    cameraVideo.style.objectFit = 'cover';
    cameraVideo.style.opacity = hideCameraFeed ? '0' : '1';
    cameraVideo.style.top = '0';
    cameraVideo.style.transition = 'opacity 420ms ease';
    cameraVideo.style.width = '100%';
    cameraVideo.style.zIndex = '0';
  }

  const canvas = mindarThree.renderer.domElement;
  mindarThree.renderer.setClearAlpha(0);
  canvas.style.background = 'transparent';
  canvas.style.inset = '0';
  canvas.style.zIndex = '1';

  if (mindarThree.cssRenderer?.domElement) {
    mindarThree.cssRenderer.domElement.style.background = 'transparent';
    mindarThree.cssRenderer.domElement.style.pointerEvents = 'none';
    mindarThree.cssRenderer.domElement.style.zIndex = '2';
  }
};

export const ScanPage = () => {
  const { publicSlug = '' } = useParams();
  const browserSupport = useMemo(() => getBrowserSupport(), []);
  const chromeUrl = useMemo(() => buildChromeOpenUrl(window.location.href), []);
  const arContainerRef = useRef<HTMLDivElement | null>(null);
  const arRuntimeRef = useRef<ArRuntime | null>(null);
  const arRunIdRef = useRef(0);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const playbackVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const videoProgressMarks = useRef(new Set<number>());
  const landingEventTrackedRef = useRef('');
  const viewerLoadTrackedRef = useRef('');
  const ctaImpressionTrackedRef = useRef('');
  const adPlayTrackedRef = useRef(new Set<AdPlayContext>());
  const videoPlayContextRef = useRef<AdPlayContext>('direct');
  const [allowUnsupportedBrowser, setAllowUnsupportedBrowser] = useState(false);
  const [mode, setMode] = useState<'ready' | 'camera' | 'video'>('ready');
  const [cameraState, setCameraState] = useState<'idle' | 'starting' | 'active' | 'recognized' | 'denied' | 'error'>('idle');
  const [showCtaOverlay, setShowCtaOverlay] = useState(false);
  const [backgroundMode, setBackgroundMode] = useState<'transparent' | 'black'>('transparent');
  const [backgroundToast, setBackgroundToast] = useState('');
  const [showScanSettings, setShowScanSettings] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const experienceQuery = useQuery({
    queryKey: ['public-experience', publicSlug],
    queryFn: () => loadExperience(publicSlug),
    enabled: Boolean(publicSlug && hasSupabaseConfig && (browserSupport.supported || allowUnsupportedBrowser)),
    retry: 1
  });

  const experience = experienceQuery.data;
  const canUseCamera = typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
  const showArtworkBackground = backgroundMode === 'transparent' && mode === 'ready' && Boolean(experience?.markerUrl);
  const usesMindAr = Boolean(experience?.mindFileUrl && experience.videoUrl);
  const showFallbackCameraPreview = mode === 'camera' && (!usesMindAr || cameraState === 'error');
  const hideTrackingMarkers = mode === 'camera' && usesMindAr && cameraState === 'recognized';
  const hideCameraFeedForAr = hideTrackingMarkers && backgroundMode === 'black';
  const cameraShadeClass = hideCameraFeedForAr ? 'scan-camera-shade-clear' : 'scan-camera-shade';
  const videoIsHls = Boolean(experience?.videoUrl && isHlsUrl(experience.videoUrl));
  const canSaveDirectVideo = Boolean(experience?.videoUrl && !videoIsHls);
  const canUseNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const ctaKicker = experience?.ctaKicker?.trim() || '';
  const ctaHeadline = experience?.ctaHeadline?.trim() || '';
  const ctaBody = experience?.ctaBody?.trim() || '';
  const ctaButtonLabel = experience?.ctaLabel?.replace(/\s*\(https?:\/\/[^)]+\)\s*$/i, '').trim() || ctaDefaults.label;
  const ctaThemeStyle = useMemo(
    () => ctaThemeCssVariables(experience?.ctaTheme) as CSSProperties,
    [experience?.ctaTheme]
  );
  const scanPageConfig = useMemo(
    () => normalizeScanPageConfig(experience?.scanPageConfig),
    [experience?.scanPageConfig]
  );
  const scanPageStyle = useMemo(
    () => scanPageConfigCssVariables(scanPageConfig) as CSSProperties,
    [scanPageConfig]
  );
  const entrySource = useMemo(() => {
    const search = new URLSearchParams(window.location.search);
    return search.get('source') || search.get('utm_source') || '';
  }, []);
  const isQrEntry = isQrEntrySource(entrySource);

  useEffect(() => {
    adPlayTrackedRef.current.clear();
    videoPlayContextRef.current = 'direct';
    videoProgressMarks.current.clear();
  }, [publicSlug]);

  useEffect(() => {
    const landingEventKey = `${publicSlug}:${entrySource || 'direct'}`;
    if (!publicSlug || !hasSupabaseConfig || landingEventTrackedRef.current === landingEventKey) return;
    landingEventTrackedRef.current = landingEventKey;
    if (isQrEntry) void trackEvent(publicSlug, 'qr_scan', { source: entrySource || 'qr' });
    void trackEvent(publicSlug, 'landing_view', { source: entrySource || 'direct' });
  }, [entrySource, isQrEntry, publicSlug]);

  useEffect(() => {
    if (!experience) return;
    const viewerLoadKey = `${publicSlug}:${experience.publicSlug}`;
    if (viewerLoadTrackedRef.current === viewerLoadKey) return;
    viewerLoadTrackedRef.current = viewerLoadKey;
    void trackEvent(publicSlug, 'viewer_loaded', {
      has_mind_file: Boolean(experience.mindFileUrl),
      has_video: Boolean(experience.videoUrl)
    });
  }, [experience, publicSlug]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previous = {
      bodyBackground: body.style.background,
      htmlBackground: html.style.background,
      overscrollBehavior: body.style.overscrollBehavior
    };

    html.style.background = '#000210';
    body.style.overscrollBehavior = 'none';
    body.style.background = experience?.markerUrl && backgroundMode === 'transparent'
      ? `#000210 url("${experience.markerUrl}") center center / cover fixed no-repeat`
      : '#000210';

    return () => {
      html.style.background = previous.htmlBackground;
      body.style.background = previous.bodyBackground;
      body.style.overscrollBehavior = previous.overscrollBehavior;
    };
  }, [backgroundMode, experience?.markerUrl]);

  useEffect(() => {
    if (!experience?.ctaUrl || mode === 'ready') {
      setShowCtaOverlay(false);
      return;
    }
    if (mode === 'camera' && cameraState !== 'recognized') return;

    setShowCtaOverlay(false);
    const timeoutId = window.setTimeout(() => setShowCtaOverlay(true), CTA_REVEAL_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [cameraState, experience, mode]);

  useEffect(() => {
    if (!backgroundToast) return;
    const timeoutId = window.setTimeout(() => setBackgroundToast(''), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [backgroundToast]);

  useEffect(() => {
    if (!showCtaOverlay || !experience?.ctaUrl) return;
    const ctaImpressionKey = `${publicSlug}:${experience.ctaUrl}`;
    if (ctaImpressionTrackedRef.current === ctaImpressionKey) return;
    ctaImpressionTrackedRef.current = ctaImpressionKey;
    void trackEvent(publicSlug, 'cta_impression', {
      cta_label: ctaButtonLabel,
      cta_headline: ctaHeadline,
      display_mode: mode
    });
  }, [ctaButtonLabel, ctaHeadline, experience?.ctaUrl, mode, publicSlug, showCtaOverlay]);

  useEffect(() => {
    if (mode === 'ready') setShowScanSettings(false);
  }, [mode]);

  useEffect(
    () => () => {
      cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
      cleanupArRuntime();
    },
    []
  );

  useEffect(() => {
    if (!arRuntimeRef.current) return;
    arRuntimeRef.current.video.muted = !soundEnabled;
    if (cameraState === 'recognized') {
      void arRuntimeRef.current.video.play().catch(() => undefined);
    }
  }, [cameraState, soundEnabled]);

  useEffect(() => {
    const runtime = arRuntimeRef.current;
    if (!runtime) return;
    styleMindArLayers(runtime.mindarThree, runtime.container, { hideCameraFeed: hideCameraFeedForAr });
  }, [hideCameraFeedForAr]);

  const cleanupArRuntime = () => {
    const runtime = arRuntimeRef.current;
    arRuntimeRef.current = null;
    if (!runtime) return;

    runtime.anchor.onTargetFound = undefined;
    runtime.anchor.onTargetLost = undefined;
    runtime.renderer.setAnimationLoop(null);
    runtime.video.pause();
    runtime.cleanupVideo();
    try {
      void Promise.resolve(runtime.mindarThree.stop()).catch(() => undefined);
    } catch {
      // MindAR can throw if startup timed out before its controller was created.
    }
    runtime.container.replaceChildren();
  };

  const recordAdPlay = (playContext: AdPlayContext, metadata: Record<string, unknown> = {}) => {
    if (!publicSlug || adPlayTrackedRef.current.has(playContext)) return;
    adPlayTrackedRef.current.add(playContext);
    void trackEvent(publicSlug, 'video_started', {
      play_context: playContext,
      ...metadata
    });
  };

  const startFallbackCamera = async (trackingState: typeof cameraState = 'active', eventMode = 'camera_fallback') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' } }
      });
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        await cameraVideoRef.current.play();
      }
      setCameraState(trackingState);
      void trackEvent(publicSlug, 'ar_started', { mode: eventMode });
    } catch {
      setCameraState('denied');
      void trackEvent(publicSlug, 'camera_permission_denied');
    }
  };

  useEffect(() => {
    if (mode !== 'camera' || !experience) return;
    const runId = arRunIdRef.current + 1;
    arRunIdRef.current = runId;

    const startAr = async () => {
      if (!canUseCamera) {
        if (experience.videoUrl) openVideo('fallback');
        return;
      }

      if (!experience.mindFileUrl || !experience.videoUrl) {
        await startFallbackCamera();
        return;
      }

      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      if (arRunIdRef.current !== runId) return;

      const container = arContainerRef.current;
      if (!container) return;

      try {
        cleanupArRuntime();
        const markerAspect = experience.markerUrl
          ? await loadImageAspect(experience.markerUrl).catch(() => null)
          : null;
        const module = (await import(/* @vite-ignore */ MINDAR_THREE_MODULE_URL)) as {
          MindARThree: MindARThreeConstructor;
        };
        if (arRunIdRef.current !== runId) return;

        const mindarThree = new module.MindARThree({
          container,
          imageTargetSrc: experience.mindFileUrl,
          filterMinCF: 0.0001,
          filterBeta: 0.001,
          uiLoading: 'no',
          uiScanning: 'no'
        });
        const { renderer, scene, camera } = mindarThree;
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(container.clientWidth, container.clientHeight);

        const anchor = mindarThree.addAnchor(0);
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.loop = true;
        video.muted = !soundEnabled;
        video.playsInline = true;
        video.preload = 'auto';
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        const cleanupVideo = attachVideoSource(video, experience.videoUrl);

        const texture = new THREE.VideoTexture(video);
        texture.colorSpace = THREE.SRGBColorSpace;
        const material = new THREE.MeshBasicMaterial({
          map: texture,
          side: THREE.DoubleSide,
          transparent: true
        });
        const initialAspect = markerAspect ?? 16 / 9;
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1 / initialAspect), material);
        mesh.visible = false;
        video.addEventListener('loadedmetadata', () => {
          const sourceAspect = video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : initialAspect;
          const targetAspect = markerAspect ?? sourceAspect;
          mesh.geometry.dispose();
          mesh.geometry = new THREE.PlaneGeometry(1, 1 / targetAspect);
          fitTextureToAspect(texture, sourceAspect, targetAspect);
        });
        anchor.group.add(mesh);

        let targetFound = false;
        let revealStarted = false;
        let stableStartFrames = 0;
        const poseScratch = createPoseScratch();
        const revealVideo = () => {
          if (revealStarted) return;
          revealStarted = true;
          mesh.visible = true;
          setCameraState('recognized');
          recordAdPlay('ar', { trigger: 'image_recognized' });
          void video.play().catch(() => undefined);
        };

        anchor.onTargetFound = () => {
          targetFound = true;
          stableStartFrames = 0;
          void trackEvent(publicSlug, 'image_recognized');
        };
        anchor.onTargetLost = () => {
          targetFound = false;
          revealStarted = false;
          stableStartFrames = 0;
          mesh.visible = false;
          setCameraState('active');
          video.pause();
        };

        arRuntimeRef.current = { anchor, cleanupVideo, container, mindarThree, renderer, video };
        const startPromise = mindarThree.start();
        const restyleTimers = [0, 250, 1000].map((delay) =>
          window.setTimeout(() => styleMindArLayers(mindarThree, container), delay)
        );
        let timeoutId = 0;
        await Promise.race([
          startPromise,
          new Promise((_, reject) => {
            timeoutId = window.setTimeout(() => reject(new Error('AR_START_TIMEOUT')), AR_START_TIMEOUT_MS);
          })
        ]).finally(() => {
          window.clearTimeout(timeoutId);
          restyleTimers.forEach((timerId) => window.clearTimeout(timerId));
        });
        if (arRunIdRef.current !== runId) return;
        styleMindArLayers(mindarThree, container, { hideCameraFeed: hideCameraFeedForAr });
        setCameraState('active');
        void trackEvent(publicSlug, 'ar_started', { mode: 'mindar_image_tracking' });
        renderer.setAnimationLoop(() => {
          if (targetFound && !revealStarted) {
            stableStartFrames = targetIsFacingCamera(anchor.group, camera, poseScratch) ? stableStartFrames + 1 : 0;
            if (stableStartFrames >= AR_STABLE_START_FRAMES) revealVideo();
          }
          renderer.render(scene, camera);
        });
      } catch (error) {
        console.warn('[scan] MindAR start failed', error);
        cleanupArRuntime();
        if (error instanceof Error && error.message === 'AR_START_TIMEOUT') {
          void trackEvent(publicSlug, 'error', { reason: 'ar_start_timeout' });
          await startFallbackCamera('error', 'mindar_start_timeout');
          return;
        }
        const name = error instanceof DOMException ? error.name : '';
        setCameraState(name === 'NotAllowedError' ? 'denied' : 'error');
        if (name === 'NotAllowedError') void trackEvent(publicSlug, 'camera_permission_denied');
      }
    };

    void startAr();

    return () => {
      arRunIdRef.current += 1;
      cleanupArRuntime();
    };
    // The tracker should start once when entering camera mode. Recognition state and sound toggles are handled separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUseCamera, experience, mode, publicSlug]);

  const stopCamera = () => {
    arRunIdRef.current += 1;
    cleanupArRuntime();
    cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
    cameraStreamRef.current = null;
    if (cameraVideoRef.current) cameraVideoRef.current.srcObject = null;
    setCameraState('idle');
    setMode('ready');
  };

  const startCamera = async () => {
    if (!canUseCamera) {
      if (experience?.videoUrl) openVideo('fallback');
      return;
    }

    setMode('camera');
    setCameraState('starting');
    void trackEvent(publicSlug, 'camera_opened');
  };

  const onStageClick = () => {
    if (mode === 'ready') void startCamera();
  };

  const selectBackgroundMode = (next: typeof backgroundMode) => {
    setBackgroundMode(next);
    setBackgroundToast(next === 'black' ? 'Black background selected' : 'Transparent background selected');
  };

  const toggleBackgroundMode = () => {
    selectBackgroundMode(backgroundMode === 'transparent' ? 'black' : 'transparent');
  };

  const openVideo = (playContext: AdPlayContext = 'direct') => {
    videoPlayContextRef.current = playContext;
    setShowScanSettings(false);
    stopCamera();
    videoProgressMarks.current.clear();
    setMode('video');
  };

  const shareOrSaveVideo = async () => {
    if (!experience?.videoUrl) return;

    try {
      if (canUseNativeShare) {
        await navigator.share({
          title: experience.adName || 'ADarveo video',
          text: 'Open this ADarveo reveal.',
          url: experience.videoUrl
        });
        setShowScanSettings(false);
        return;
      }
    } catch {
      return;
    }

    const link = document.createElement('a');
    link.href = experience.videoUrl;
    link.target = '_blank';
    link.rel = 'noreferrer';
    if (canSaveDirectVideo) link.download = `${experience.publicSlug || 'adarveo-video'}.mp4`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setBackgroundToast(canSaveDirectVideo ? 'Save started' : 'Video opened');
    setShowScanSettings(false);
  };


  const onVideoTimeUpdate = (event: SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    if (!video.duration || !Number.isFinite(video.duration)) return;
    const progress = Math.floor((video.currentTime / video.duration) * 100);
    for (const mark of [25, 50, 75]) {
      if (progress >= mark && !videoProgressMarks.current.has(mark)) {
        videoProgressMarks.current.add(mark);
        void trackEvent(publicSlug, 'video_progress', { progress });
      }
    }
  };

  const openFullscreenVideo = async () => {
    const video = playbackVideoRef.current as FullscreenVideoElement | null;
    if (!video) return;

    try {
      if (typeof video.webkitEnterFullscreen === 'function') {
        video.webkitEnterFullscreen();
      } else if (typeof video.requestFullscreen === 'function') {
        await video.requestFullscreen();
      }
    } catch {
      // Some browsers only allow fullscreen from native video controls.
    }
  };

  if (!browserSupport.supported && !allowUnsupportedBrowser) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#000210] px-5 text-white">
        <section className="max-w-lg rounded-lg border border-white/14 bg-white/8 p-6">
          <p className="text-sm font-semibold uppercase text-blue-200">Best in Chrome or Safari</p>
          <h1 className="mt-3 font-heading text-4xl font-semibold">Open in Chrome for the best camera experience</h1>
          <p className="mt-4 leading-7 text-white/70">
            This interactive ad works best in Chrome or Safari. You are currently using {browserSupport.name}, which may
            block the camera.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <a className="rounded-md bg-primary px-4 py-3 text-center font-semibold" href={chromeUrl}>
              Open with Chrome
            </a>
            <button
              className="rounded-md border border-white/18 px-4 py-3 font-semibold"
              onClick={() => setAllowUnsupportedBrowser(true)}
              type="button"
            >
              Continue anyway
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="scan-page text-white" style={scanPageStyle}>
      {experienceQuery.isLoading && (
        <section className="scan-page-fill flex items-center justify-center text-white/72">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Opening your experience...
        </section>
      )}

      {experienceQuery.isError && (
        <section className="mx-auto max-w-xl px-5 py-16">
          <div className="rounded-lg border border-white/14 bg-white/8 p-6">
            <p className="text-sm font-semibold uppercase text-red-200">Link unavailable</p>
            <h1 className="mt-3 font-heading text-4xl font-semibold">This experience is not ready yet</h1>
            <p className="mt-4 text-white/70">Please try again soon.</p>
          </div>
        </section>
      )}

      {experience && (
        <section className="scan-page-stage" onClick={onStageClick}>
          {showArtworkBackground && (
            <img alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-55" src={experience.markerUrl ?? ''} />
          )}

          {mode === 'camera' && (
            <video
              ref={cameraVideoRef}
              autoPlay
              className={`absolute inset-0 h-full w-full object-cover transition-opacity ${
                showFallbackCameraPreview ? 'opacity-100' : 'opacity-0'
              }`}
              muted
              playsInline
            />
          )}

          {mode === 'camera' && usesMindAr && cameraState !== 'error' && (
            <div ref={arContainerRef} className="absolute inset-0 h-full w-full overflow-hidden bg-transparent" />
          )}

          {mode === 'video' && experience.videoUrl && (
            <video
              ref={playbackVideoRef}
              autoPlay
              className="absolute inset-0 h-full w-full bg-black object-contain"
              controls
              loop
              muted={!soundEnabled}
              onEnded={() => void trackEvent(publicSlug, 'video_completed')}
              onPlay={() => recordAdPlay(videoPlayContextRef.current)}
              onTimeUpdate={onVideoTimeUpdate}
              playsInline
              src={experience.videoUrl}
            />
          )}

          <div className={`absolute inset-0 ${mode === 'camera' ? cameraShadeClass : 'scan-ready-shade'}`} />

          <div className="scan-top-actions">
            <button
              aria-label={soundEnabled ? 'Mute sound' : 'Unmute sound'}
              className="scan-icon-button scan-sound-button"
              onClick={(event) => {
                event.stopPropagation();
                setSoundEnabled((enabled) => !enabled);
              }}
              type="button"
            >
              {soundEnabled ? <Volume2 className="h-6 w-6" /> : <VolumeX className="h-6 w-6" />}
            </button>
            {mode === 'ready' ? (
              <button
                aria-label={backgroundMode === 'transparent' ? 'Select black background' : 'Select transparent background'}
                className="scan-icon-button scan-settings-button"
                onClick={(event) => {
                  event.stopPropagation();
                  toggleBackgroundMode();
                }}
                type="button"
              >
                <span
                  key={backgroundMode}
                  aria-hidden="true"
                  className={`scan-background-icon ${
                    backgroundMode === 'transparent' ? 'scan-background-icon-filled' : 'scan-background-icon-transparent'
                  }`}
                />
              </button>
            ) : (
              <button
                aria-expanded={showScanSettings}
                aria-label="Open scan settings"
                className={`scan-icon-button scan-settings-button ${showScanSettings ? 'scan-icon-button-active' : ''}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowScanSettings((shown) => !shown);
                }}
                type="button"
              >
                <Settings className="h-6 w-6" />
                <span
                  aria-hidden="true"
                  className={`scan-settings-badge ${
                    backgroundMode === 'black' ? 'scan-settings-badge-black' : 'scan-settings-badge-transparent'
                  }`}
                />
              </button>
            )}
          </div>

          {backgroundToast && (
            <div aria-live="polite" className="scan-toast" role="status">
              {backgroundToast}
            </div>
          )}

          {mode !== 'ready' && showScanSettings && (
            <div
              className="scan-settings-scrim"
              onClick={(event) => {
                event.stopPropagation();
                setShowScanSettings(false);
              }}
            >
              <section
                aria-label="Scan settings"
                className="scan-settings-sheet"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
              >
                <div className="scan-sheet-handle" />
                <div className="scan-sheet-header">
                  <h2>Scan settings</h2>
                  <button aria-label="Close scan settings" className="scan-sheet-close" onClick={() => setShowScanSettings(false)} type="button">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="scan-sheet-section">
                  <p className="scan-sheet-label">Background</p>
                  <div className="scan-background-segment">
                    <button
                      aria-pressed={backgroundMode === 'transparent'}
                      className={`scan-segment-button ${backgroundMode === 'transparent' ? 'scan-segment-button-active' : ''}`}
                      onClick={() => selectBackgroundMode('transparent')}
                      type="button"
                    >
                      <span aria-hidden="true" className="scan-segment-swatch scan-segment-swatch-transparent" />
                      Transparent
                    </button>
                    <button
                      aria-pressed={backgroundMode === 'black'}
                      className={`scan-segment-button ${backgroundMode === 'black' ? 'scan-segment-button-active' : ''}`}
                      onClick={() => selectBackgroundMode('black')}
                      type="button"
                    >
                      <span aria-hidden="true" className="scan-segment-swatch scan-segment-swatch-black" />
                      Black
                    </button>
                  </div>
                </div>

                <div className="scan-sheet-actions">
                  {mode === 'video' && experience.videoUrl && (
                    <button
                      className="scan-sheet-action"
                      onClick={() => {
                        setShowScanSettings(false);
                        void openFullscreenVideo();
                      }}
                      type="button"
                    >
                      <Maximize2 className="h-5 w-5" />
                      Fullscreen
                    </button>
                  )}
                  {experience.videoUrl && mode !== 'video' && (
                    <button
                      className="scan-sheet-action"
                      onClick={() => openVideo(['denied', 'error'].includes(cameraState) ? 'fallback' : 'direct')}
                      type="button"
                    >
                      <Play className="h-5 w-5" />
                      Watch video
                    </button>
                  )}
                  {experience.videoUrl && (
                    <button className="scan-sheet-action" onClick={() => void shareOrSaveVideo()} type="button">
                      {canSaveDirectVideo ? <Download className="h-5 w-5" /> : <Share2 className="h-5 w-5" />}
                      {canSaveDirectVideo ? 'Save video' : 'Share video'}
                    </button>
                  )}
                  {mode === 'camera' && (
                    <button className="scan-sheet-action scan-sheet-action-danger" onClick={stopCamera} type="button">
                      <X className="h-5 w-5" />
                      Stop camera
                    </button>
                  )}
                </div>
              </section>
            </div>
          )}

          {mode !== 'video' && (
            <div className={`scan-reticle-layer ${hideTrackingMarkers ? 'scan-reticle-layer-hidden' : 'scan-reticle-layer-visible'}`}>
              <div className={`scan-reticle scan-reticle-${scanPageConfig.crosshair}`} aria-hidden="true">
                <span className="scan-corner scan-corner-tl" />
                <span className="scan-corner scan-corner-tr" />
                <span className="scan-corner scan-corner-bl" />
                <span className="scan-corner scan-corner-br" />
                <span className="scan-circle" />
                <span className="scan-focus scan-focus-top" />
                <span className="scan-focus scan-focus-right" />
                <span className="scan-focus scan-focus-bottom" />
                <span className="scan-focus scan-focus-left" />
                <span className="scan-sweep" />
                <span className="scan-pulse" />
                <Sparkles className="scan-center-glyph" />
              </div>
            </div>
          )}

          <div className="scan-page-fill relative z-30 flex flex-col items-center justify-end px-5 pb-[calc(5rem+env(safe-area-inset-bottom))] pt-36 text-center">
            {mode === 'ready' && (
              <div className="scan-ready-content">
                <h1 className="scan-ready-title">{scanPageConfig.headline}</h1>
                <p className="scan-ready-copy">{scanPageConfig.instructions}</p>
                <button
                  className="scan-start-pill"
                  disabled={!canUseCamera && !experience.videoUrl}
                  onClick={(event) => {
                    event.stopPropagation();
                    void startCamera();
                  }}
                  type="button"
                >
                  <Sparkles className="h-5 w-5" />
                  {scanPageConfig.buttonLabel}
                </button>
              </div>
            )}

            {mode === 'camera' && ['starting', 'denied', 'error'].includes(cameraState) && (
              <div className="scan-status-card">
                {cameraState === 'starting' && (
                  <p className="flex items-center gap-2 text-white/78">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Starting image tracking...
                  </p>
                )}
                {cameraState === 'denied' && (
                  <>
                    <p className="flex items-center gap-2 font-semibold text-red-100">
                      <CircleAlert className="h-4 w-4" />
                      Camera permission denied
                    </p>
                    <p className="mt-2 text-sm text-white/68">You can still watch the video.</p>
                  </>
                )}
                {cameraState === 'error' && (
                  <>
                    <p className="flex items-center gap-2 font-semibold text-red-100">
                      <CircleAlert className="h-4 w-4" />
                      Image tracking could not start
                    </p>
                    <p className="mt-2 text-sm text-white/68">You can still watch the video.</p>
                  </>
                )}
              </div>
            )}
          </div>

          {mode === 'camera' && (
            <p className="absolute inset-x-0 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-30 text-center text-sm text-white/44">
              Powered by <span className="text-white/70">ADarveo</span>
            </p>
          )}

          {showCtaOverlay && experience.ctaUrl && (
            <div className="scan-cta-shell">
              <section
                aria-label={ctaHeadline || ctaButtonLabel || 'Call to action'}
                className="scan-cta-card"
                style={ctaThemeStyle}
              >
                <button
                  aria-label="Close"
                  className="scan-cta-close"
                  onClick={() => setShowCtaOverlay(false)}
                  type="button"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="scan-cta-layout">
                  <div className="scan-cta-spark" aria-hidden="true">
                    {experience.ctaLogoUrl ? (
                      <img alt="" className="scan-cta-logo" src={experience.ctaLogoUrl} />
                    ) : (
                      <Sparkles className="h-5 w-5" />
                    )}
                  </div>
                  <div className="scan-cta-copy">
                    {ctaKicker && <p className="scan-cta-kicker">{ctaKicker}</p>}
                    {ctaHeadline && <h2>{ctaHeadline}</h2>}
                    {ctaBody && <p className="scan-cta-body">{ctaBody}</p>}
                  </div>
                </div>

                <a
                  className="scan-cta-link"
                  href={experience.ctaUrl}
                  onClick={() => void trackEvent(publicSlug, 'cta_clicked', { action: 'primary_cta' })}
                  rel="noreferrer"
                  target="_blank"
                >
                  {ctaButtonLabel}
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </section>
            </div>
          )}

        </section>
      )}
    </main>
  );
};
